export type Story = {
  slug: string;
  title: string;
  excerpt: string;
  meta: string;
  category: string;
  cover: string;
  body: string[];
  gallery: string[];
  related?: string[];
};

// Dedicated Northvale Journal imagery
const J = {
  featured: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/b20006f5-a812-4e65-8f25-9e4f4ada7a67_journal-featured-v2.webp",
  materials: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bf5b8695-9a20-443d-be72-d76973c46453_journal-materials-v3.webp",
  fieldNotes: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_journal-field-notes.webp",
  gear: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_journal-gear.webp",
  care: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_journal-care.webp",
  people: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_journal-people.webp",
};

export const stories: Story[] = [
  {
    slug: "alta-via-two-hundred",
    title: "Two hundred kilometers of quiet weather along the Alta Via.",
    excerpt: "Our field team spent the shoulder season retracing an alpine route in shell, midlayer and pack.",
    meta: "Oct 2026 · 8 min read",
    category: "Field Notes",
    cover: J.featured,
    body: [
      "We started the traverse in early October, when the huts were closing and the ridge was already dusted with the first real snow of the season. Two hundred kilometers is a long way to move quietly, especially in weather that keeps changing its mind.",
      "For nine days, we tested the AW26 shell against sideways rain, wet snow, and the kind of wind that finds the smallest opening in a hood. We tested the fleece against dawns at 2,400 meters and against cafés at 400. We tested the pack against everything.",
      "The gear you see in the release today came back changed. Small changes — a re-shaped hood brim, a re-routed hip belt, a quieter zipper pull — but they came from actual miles under weather. That is the only kind of design we know how to trust.",
    ],
    gallery: [],
    related: ["fabric-under-rain", "coastal-quiet"],
  },
  {
    slug: "fabric-under-rain",
    title: "Fabric under rain — a material story.",
    excerpt: "How the 3-layer shell earns its finish, one wet afternoon at a time.",
    meta: "Sep 2026 · 5 min read",
    category: "Materials",
    cover: J.materials,
    body: [
      "A water repellency finish is not permanent. It is a relationship, one you renew across seasons.",
      "We designed the Lightweight Shell knowing that most owners will re-treat it at least once. The way the shell sheds water on day one should be the way it sheds water in year five.",
    ],
    gallery: [],
    related: ["alta-via-two-hundred", "seam-by-seam"],
  },
  {
    slug: "coastal-quiet",
    title: "Coastal quiet — testing footwear on wet rock.",
    excerpt: "A week on the Atlantic edge with the Traverse trail shoe.",
    meta: "Sep 2026 · 6 min read",
    category: "Field Notes",
    cover: J.fieldNotes,
    body: [
      "Wet rock is honest. It tells you immediately whether an outsole is designed by people who go outside, or by people who wish they did.",
      "We spent a week on a section of coast where the trail moves in and out of the tideline. The Traverse never once let us second-guess a step.",
    ],
    gallery: [],
    related: ["alta-via-two-hundred", "packing-light"],
  },
  {
    slug: "packing-light",
    title: "Packing light for a three-day traverse.",
    excerpt: "What stays in the pack, what comes out, and why less is often more capable.",
    meta: "Aug 2026 · 4 min read",
    category: "Gear",
    cover: J.gear,
    body: [
      "The lightest pack is the one you do not notice on hour seven. Here is how we build a load that disappears.",
      "Layer discipline matters more than weight numbers. Two thoughtful pieces beat four hopeful ones.",
    ],
    gallery: [],
    related: ["alta-via-two-hundred"],
  },
  {
    slug: "seam-by-seam",
    title: "Seam by seam — inside the Lightweight Shell construction.",
    excerpt: "A closer look at how a 3-layer shell is put together and why every taped joint counts.",
    meta: "Aug 2026 · 6 min read",
    category: "Materials",
    cover: J.care,
    body: [
      "A shell is only as waterproof as its weakest seam. We walk through the taping process one joint at a time.",
    ],
    gallery: [],
    related: ["fabric-under-rain"],
  },
  {
    slug: "high-route-dolomites",
    title: "The high route — five passes above the Dolomites.",
    excerpt: "A slow week on an old shepherds' path, mapped for anyone who wants to try it.",
    meta: "Jul 2026 · 9 min read",
    category: "Routes",
    cover: J.people,
    body: [
      "Five passes, four huts, one very old path. Here is the full route with waypoints and notes.",
    ],
    gallery: [],
    related: ["alta-via-two-hundred"],
  },
  {
    slug: "people-of-the-trail",
    title: "People of the trail — a hut warden's season.",
    excerpt: "One warden, one hut at 2,200 meters, and 148 days of weather.",
    meta: "Jul 2026 · 5 min read",
    category: "People",
    cover: J.people,
    body: [
      "A conversation with Maren, who runs a small alpine hut from May to October.",
    ],
    gallery: [],
    related: ["high-route-dolomites"],
  },
  {
    slug: "care-and-repair",
    title: "Care and repair — extending the life of a shell.",
    excerpt: "Simple wash, re-treat and small-repair habits that add years to a technical jacket.",
    meta: "Jun 2026 · 4 min read",
    category: "Gear",
    cover: J.care,
    body: [
      "The most sustainable jacket is the one you already own. Here is how to keep it working.",
    ],
    gallery: [],
    related: ["fabric-under-rain", "seam-by-seam"],
  },
];

export function getStory(slug: string) {
  return stories.find((s) => s.slug === slug);
}